import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import { DESKTOP_VIEWPORT } from '../../hooks/useViewport';
import Toast from '../../utils/toast'
import { useAuth } from '../../hooks/useAuth';
import logo from '../../assets/main/logo.d99exch.png';
import { useSiteBrand } from '../../siteBrand';
import './TwoFactorAuth.css';

const TwoFactorAuth = () => {
  const site = useSiteBrand(); // Owner Panel logo (siteBrand.js); built-in when not uploaded
    const location = useLocation();
    const navigate = useNavigate();
    const { verify2FA, isVerifying2FA, isAuthenticated, user } = useAuth();
    
    // State from navigation
    const { staffId, ownerId, method } = location.state || {};
    // An app code is already on the phone: no bot pushed it, and there is
    // nothing to resend.
    const isAppCode = method === 'android' || method === 'google';
    
    // OTP State
    const [otp, setOtp] = useState(new Array(6).fill(""));
    const inputRefs = useRef([]);


    useEffect(() => {
        // Redirect if no pending 2FA session (direct access prevention)
        if (!staffId && !ownerId) {
            navigate('/login');
        }
    }, [staffId, ownerId, navigate]);

    useEffect(() => {
        if (isAuthenticated) {
            if (sessionStorage.getItem('needPasswordChange') === 'true') {
                navigate('/reset-password', { replace: true })
            } else if (user) {
                navigate('/admin/market-analysis', { replace: true })
            }
        }
    }, [isAuthenticated, user, navigate]);


    const handleVerify = async (enteredOtp) => {
        try {
            const verifyData = {
                otp: enteredOtp,
                staffId,
                ownerId
            };

            const response = await verify2FA(verifyData);

            if (response?.success) {
                Toast.fire({
                    icon: 'success',
                    title: 'Success'
                });
                // Navigation handled handled by useEffect on isAuthenticated change or onSuccess in useAuth
            }
        } catch (error) {
            const msg = error?.response?.data?.error || 'Verification failed';
            Toast.fire({
                icon: 'error',
                title: msg
            });
            // Clear OTP on error? Maybe not all of it.
        }
    };

    // OTP Input handlers
    const handleChange = (element, index) => {
        if (isNaN(element.value)) return false;
        
        const newOtp = [...otp];
        newOtp[index] = element.value;
        setOtp(newOtp); // Update state immediately for visual feedback
        
        // Focus next input
        if (element.value !== "" && element.nextSibling) {
            element.nextSibling.focus();
        }

        // Check for complete OTP
        if (newOtp.every(val => val !== "")) {
            handleVerify(newOtp.join(""));
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").slice(0, 6);
        if (!/^\d+$/.test(pastedData)) return; // Only allow digits

        const newOtp = [...otp];
        pastedData.split("").forEach((char, i) => {
            if (i < 6) newOtp[i] = char;
        });
        setOtp(newOtp);
        
        // Focus appropriate box
        const nextIndex = Math.min(pastedData.length, 5);
        if(inputRefs.current[nextIndex]) {
             inputRefs.current[nextIndex].focus();
        }

        if (newOtp.every(val => val !== "")) {
            handleVerify(newOtp.join(""));
        }
    };

    // Copy is the reference's own, verbatim (casing included).
    const title = isAppCode
        ? 'Security Code Verification'
        : 'Security Code Verification Using Telegram App';
    const subtitle = isAppCode
        ? (method === 'android'
            ? 'Enter 6-digit code from your security auth verification App'
            : 'Enter 6-digit code from your security google auth verification App')
        : 'Enter 6-digit code from your telegram bot';

    return (
        // Reference markup (its style.css:8301 `.login-auth` block): themed full-screen page,
        // logo in `.log-logo`, copy + OTP form in `.featured-box-login`. Colours come from the
        // panel theme variables (--bg-primary / --text-primary / --bg-secondary), never hex.
        <div className="login-auth two-factor-auth-page">
            <Helmet>
                <meta name="viewport" content={DESKTOP_VIEWPORT} />
            </Helmet>
            <div>
                <div className="log-logo">
                    <img src={site.logo || logo} alt="" />
                </div>
                <div className="featured-box-login">
                    <h3 className="text-center">{title}</h3>
                    <div className="mt-3 text-center">
                        {subtitle}
                        {!isAppCode && <span className="resend-text"> Resend Code</span>}
                    </div>
                    <form className="mt-3" role="form" autoComplete="off" method="post" onSubmit={(e) => e.preventDefault()}>
                        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }} onPaste={handlePaste}>
                            {otp.map((data, index) => (
                                <input
                                    key={index}
                                    className="otp-input"
                                    type="tel"
                                    maxLength="1"
                                    value={data}
                                    ref={(el) => inputRefs.current[index] = el}
                                    onChange={(e) => handleChange(e.target, index)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    onFocus={(e) => e.target.select()}
                                    disabled={isVerifying2FA}
                                />
                            ))}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default TwoFactorAuth;
